import React, { useState, useEffect } from 'react';
import {
  Save, X, DollarSign, Users, ArrowUp, Info,
  Loader2, ChevronRight, ChevronLeft, AlertCircle
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useToast, ToastContainer } from '../common/Toast';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import FormStepper from '../common/FormStepper';
import PreContractService from '../../services/preContractService';

//////////////////////////////////////////////////////////////
// STEPS (UI ONLY)
//////////////////////////////////////////////////////////////

const STEPS = [
  { id: 0, label: 'Role Details', icon: Users },
  { id: 1, label: 'Limits', icon: DollarSign },
  { id: 2, label: 'Hierarchy', icon: ArrowUp },
  { id: 3, label: 'Review', icon: Info },
];

//////////////////////////////////////////////////////////////
// SAME RFP STYLES
//////////////////////////////////////////////////////////////

const styles = {
  pageContainer: { padding: '24px' },
  card: {
    background: 'white',
    border: '1px solid var(--border-color)',
    boxShadow: 'var(--shadow-sm)',
    overflow: 'hidden',
  },
  cardBody: { padding: '32px' },
  formGroup: { marginBottom: '24px' },
  formLabel: {
    display: 'block',
    marginBottom: '8px',
    fontWeight: 500,
    fontSize: '14px',
  },
  required: { color: '#dc2626', marginLeft: '4px' },
  infoBanner: {
    display: 'flex',
    alignItems: 'center',
    padding: '16px 24px',
    backgroundColor: 'var(--accent-color)',
    border: '1px solid var(--border-color)',
    marginBottom: '24px',
  },
  spinning: {
    animation: 'spin 1s linear infinite',
  },
};

//////////////////////////////////////////////////////////////
// COMPONENT
//////////////////////////////////////////////////////////////

const CreateDoA = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  const { success, error: showError, toasts, removeToast } = useToast();

  const [currentStep, setCurrentStep] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [formData, setFormData] = useState({
    role: '',
    approvalLimit: '',
    currency: 'USD',
    rank: 1,
    isActive: true
  });

  //////////////////////////////////////////////////////////////
  // ORIGINAL LOGIC (UNCHANGED)
  //////////////////////////////////////////////////////////////

  useEffect(() => {
    const fetchRule = async () => {
      if (!isEditMode) return;
      setIsLoading(true);
      try {
        const response = await PreContractService.getDoA(id);
        if (response.data) {
          setFormData({
            role: response.data.role || '',
            approvalLimit: response.data.approvalLimit || '',
            currency: response.data.currency || 'USD',
            rank: response.data.rank || 1,
            isActive: response.data.isActive ?? true
          });
        }
      } catch (err) {
        console.error(err);
        showError("Failed to load rule details.");
        navigate('/contracts/pre-contract/doa');
      } finally {
        setIsLoading(false);
      }
    };
    fetchRule();
  }, [id, isEditMode, navigate]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.role || !formData.approvalLimit || !formData.rank) {
      showError("Please fill in all required fields.");
      return;
    }

    setIsSaving(true);
    try {
      if (isEditMode) {
        await PreContractService.updateDoA(id, formData);
        success("DoA rule updated successfully");
      } else {
        await PreContractService.createDoA(formData);
        success("DoA rule created successfully");
      }
      setTimeout(() => {
        setIsSaving(false);
        navigate('/contracts/pre-contract/doa');
      }, 1000);
    } catch (err) {
      console.error(err);
      showError(err.response?.data?.message || "Failed to save rule.");
      setIsSaving(false);
    }
  };

  //////////////////////////////////////////////////////////////
  // STEP NAVIGATION (UI ONLY)
  //////////////////////////////////////////////////////////////

  const handleNext = (e) => {
    if (e) e.preventDefault();
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(s => s + 1);
      window.scrollTo(0, 0);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(s => s - 1);
      window.scrollTo(0, 0);
    }
  };

  //////////////////////////////////////////////////////////////
  // STEP CONTENT
  //////////////////////////////////////////////////////////////

  const renderStep0 = () => (
    <div style={styles.formGroup}>
      <label style={styles.formLabel}>
        Role Title <span style={styles.required}>*</span>
      </label>
      <input
        type="text"
        name="role"
        className="form-control"
        value={formData.role}
        onChange={handleChange}
      />
    </div>
  );

  const renderStep1 = () => (
    <div style={{ display: 'flex', gap: '20px' }}>
      <div style={{ flex: 1 }}>
        <label style={styles.formLabel}>
          Approval Limit <span style={styles.required}>*</span>
        </label>
        <input
          type="number"
          name="approvalLimit"
          className="form-control"
          value={formData.approvalLimit}
          onChange={handleChange}
        />
      </div>

      <div style={{ flex: 1 }}>
        <label style={styles.formLabel}>Currency</label>
        <select
          className="form-control"
          name="currency"
          value={formData.currency}
          onChange={handleChange}
        >
          <option value="USD">USD</option>
          <option value="EUR">EUR</option>
          <option value="GBP">GBP</option>
          <option value="INR">INR</option>
        </select>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <>
      <div style={styles.formGroup}>
        <label style={styles.formLabel}>
          Rank <span style={styles.required}>*</span>
        </label>
        <input
          type="number"
          name="rank"
          className="form-control"
          value={formData.rank}
          onChange={handleChange}
        />
      </div>

      <div style={styles.formGroup}>
        <label style={styles.formLabel}>Status</label>
        <input
          type="checkbox"
          name="isActive"
          checked={formData.isActive}
          onChange={handleChange}
        />
        <span style={{ marginLeft: '10px' }}>
          {formData.isActive ? 'Active' : 'Inactive'}
        </span>
      </div>
    </>
  );

  const renderStep3 = () => (
    <div style={styles.infoBanner}>
      <strong>{formData.role || 'Role'}</strong> • {formData.approvalLimit} {formData.currency}
    </div>
  );

  const stepRenderers = [renderStep0, renderStep1, renderStep2, renderStep3];

  //////////////////////////////////////////////////////////////
  // HEADER ACTIONS (UNCHANGED)
  //////////////////////////////////////////////////////////////

  const headerActions = [
    { label: 'Cancel', icon: <X size={18} />, onClick: () => navigate('/contracts/pre-contract/doa'), variant: 'outline' },
    { label: isSaving ? 'Saving...' : (isEditMode ? 'Update Rule' : 'Save Rule'), icon: <Save size={18} />, onClick: handleSave, variant: 'primary', disabled: isSaving || isLoading }
  ];

  if (isLoading) return <div className="p-5 text-center">Loading...</div>;

  //////////////////////////////////////////////////////////////
  // UI
  //////////////////////////////////////////////////////////////

  return (
    <>
      <PageBreadcrumb />
      <PageHeader
        title={isEditMode ? "Edit DoA Rule" : "Create DoA Rule"}
        description="Configure authority limits for a specific role."
        actions={headerActions}
      />

      <div style={styles.pageContainer}>

        <div style={styles.card}>
          <FormStepper
            steps={STEPS}
            currentStep={currentStep}
            onStepClick={(i) => i <= currentStep && setCurrentStep(i)}
          />

          <div style={styles.cardBody}>
            <form onSubmit={currentStep === 3 ? handleSave : handleNext}>
              {stepRenderers[currentStep]()}

              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginTop: '40px',
                borderTop: '1px solid var(--border-color)',
                paddingTop: '24px'
              }}>
                <div>
                  {currentStep > 0 && (
                    <button type="button" onClick={handleBack} className="btn btn-secondary">
                      <ChevronLeft size={16} /> Back
                    </button>
                  )}
                </div>

                <div>
                  {currentStep < 3 ? (
                    <button type="submit" className="btn btn-primary">
                      Next <ChevronRight size={16} />
                    </button>
                  ) : (
                    <button type="submit" className="btn btn-primary">
                      {isSaving ? 'Saving...' : 'Save Rule'}
                    </button>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>

        {/* HELPER (UNCHANGED POSITION) */}
        <div style={{ marginTop: '24px' }}>
          <div className="card bg-light border-0 shadow-sm">
            <div className="card-body p-4">
              <h6 className="fw-bold d-flex align-items-center gap-2 mb-3">
                <Info size={18} className="text-primary" />
                DoA Helper
              </h6>

              <p className="small text-secondary mb-3">
                <strong className="text-dark">Role:</strong> The exact job title that matches your user directory.
              </p>

              <p className="small text-secondary mb-0">
                <strong>Rank:</strong> Used to determine the approval chain order. Lower ranks approve first.
              </p>
            </div>
          </div>
        </div>

      </div>

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default CreateDoA;